/**
 * cardAdapter.js - Maps cards_v2.json → UI-ready shape
 *
 * All UI components consume the adapted shape. This is the single
 * translation layer between the data model and the presentation layer.
 */

/**
 * Returns days since a date string, or null if invalid.
 */
function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Freshness status based on last_verified_at.
 * Returns 'verified' (<=30d), 'recent' (<=90d), or 'needs_review' (>90d).
 */
export function getFreshnessStatus(card) {
  const days = daysSince(card.data_ops?.last_verified_at);
  if (days === null) return 'needs_review';
  if (days <= 30) return 'verified';
  if (days <= 90) return 'recent';
  return 'needs_review';
}

/**
 * Freshness display label.
 */
export function getFreshnessLabel(card) {
  const status = getFreshnessStatus(card);
  switch (status) {
    case 'verified': return 'Verificado';
    case 'recent': return 'Reciente';
    case 'needs_review': return 'En revisión';
    default: return 'En revisión';
  }
}

/**
 * Format reward type from v2 currency field.
 */
function mapRewardType(currency) {
  switch (currency) {
    case 'CASHBACK': return 'cashback';
    case 'POINTS': return 'points';
    case 'MILES': return 'miles';
    case 'NONE': return 'none';
    default: return 'other';
  }
}

/**
 * Build rewards text from v2 structured data.
 */
function buildRewardsText(rewards) {
  if (!rewards) return '';
  const notes = rewards.redemption?.notes;
  if (notes) return notes;
  if (rewards.earning_rules?.length > 0) {
    return rewards.earning_rules
      .map((r) => `${r.rate}${r.unit === 'POINTS_PER_DOLLAR' ? 'x' : '%'} ${r.category.toLowerCase()}`)
      .join(', ');
  }
  if (rewards.base_earn_rate > 0) {
    return `${rewards.base_earn_rate}${rewards.base_earn_unit === 'POINTS_PER_DOLLAR' ? 'x' : '%'} on everything`;
  }
  return '';
}

/**
 * Adapt a v2 card → UI-ready flat shape used by all CreditCardComparison components.
 */
export function adaptCard(v2Card) {
  const fee = v2Card.fees.annual_fee.amount;
  const feeDisplay = fee === 0 ? 'No annual fee' : `$${fee.toFixed(2)}`;

  return {
    // Identity
    id: v2Card.card_id,
    slug: v2Card.slug,
    name: v2Card.name,
    bank: v2Card.issuer_id,
    issuer_id: v2Card.issuer_id,
    network: v2Card.network,
    segments: v2Card.segments || [],

    // Display fields (backward-compatible with v1 shape)
    annual_fee: feeDisplay,
    purchase_rate: v2Card.rates.purchase_apr_percent != null ? `${v2Card.rates.purchase_apr_percent}%` : 'N/A',
    cash_advance_rate: v2Card.rates.cash_advance_apr_percent != null ? `${v2Card.rates.cash_advance_apr_percent}%` : 'N/A',
    balance_transfer_rate: v2Card.rates.balance_transfer_apr_percent != null ? `${v2Card.rates.balance_transfer_apr_percent}%` : 'N/A',
    foreign_conversion_fee: v2Card.fees.fx_fee_percent != null ? `${v2Card.fees.fx_fee_percent.toFixed(4)}% of value` : 'N/A',
    min_income_personal: v2Card.eligibility.min_income_personal === 0 ? 'Not required' : String(v2Card.eligibility.min_income_personal),
    min_income_household: v2Card.eligibility.min_income_household ? String(v2Card.eligibility.min_income_household) : 'N/A',
    newcomer_friendly: v2Card.eligibility.newcomer_friendly,
    rewards: buildRewardsText(v2Card.rewards) || 'No rewards',

    // Insurance (backward-compatible)
    insurance: {
      mobile_device: v2Card.features.insurance.mobile_device ? (v2Card.features.insurance.notes || 'Included') : 'Not available',
      travel_medical: v2Card.features.insurance.travel_medical ? 'Included' : 'Not available',
      rental_vehicle: v2Card.features.insurance.rental_vehicle ? 'Included' : 'Not available',
      purchase_protection: v2Card.features.insurance.purchase_protection ? 'Included' : 'Not available',
      travel_cancellation: v2Card.features.insurance.trip_cancellation ? 'Included' : 'Not available',
    },

    // Links
    affiliate_link: v2Card.commercial.apply_url || '#',
    image_url: v2Card._legacy?.image_url || null,

    // v2-specific fields (new features)
    v2: {
      fees: v2Card.fees,
      rates: v2Card.rates,
      eligibility: v2Card.eligibility,
      welcome_bonus: v2Card.welcome_bonus,
      rewards_structured: v2Card.rewards,
      features: v2Card.features,
      score: v2Card.score,
      commercial: v2Card.commercial,
      status: v2Card.status,
      seo: v2Card.seo,
      data_ops: v2Card.data_ops,
      freshness: getFreshnessStatus(v2Card),
      freshness_label: getFreshnessLabel(v2Card),
      reward_type: mapRewardType(v2Card.rewards?.currency),
      first_year_waived: v2Card.fees.annual_fee.first_year_waived,
      fx_fee_zero: v2Card.fees.fx_fee_percent === 0,
      is_secured: v2Card.eligibility.secured,
      is_student: v2Card.eligibility.student,
    },
  };
}

/**
 * Adapt the full v2 dataset → array of UI-ready cards + metadata.
 */
export function adaptDataset(v2Data) {
  const cards = v2Data.cards
    .filter((c) => c.status.is_active)
    .map(adaptCard);

  // Build issuer name lookup
  const issuerNames = {};
  for (const issuer of v2Data.issuers || []) {
    issuerNames[issuer.issuer_id] = issuer.name;
  }

  // Replace issuer_id with display name in bank field
  for (const card of cards) {
    card.bank = issuerNames[card.issuer_id] || card.issuer_id;
  }

  return {
    cards,
    issuers: v2Data.issuers || [],
    issuerNames,
    disclosure: v2Data.disclosure || {},
    schema_version: v2Data.schema_version,
  };
}
